// Synchronous re-click guard for KYC wizard action buttons (EPCH-404).
// wire:loading.attr="disabled" only disables a button *after* a request has
// started and the DOM re-morphs — a fast double click/tap can fire a second
// full request off the same pre-response snapshot before that happens. This
// guard closes the gap on the same tick as the click itself.
//
// Must be a STATIC import from app.js, not routed through the dynamic
// import()-based ./modules/index.js barrel. Livewire's classic
// (non-deferred) <script> (@livewireScripts) defers Alpine.start() /
// 'alpine:init' to 'DOMContentLoaded', which fires only after every
// deferred/type="module" script (including @vite's app.js entry) has
// already run — confirmed in vendor/livewire/livewire/dist/livewire.js
// around line 11146. A dynamic import().then(...) (the modules/index.js
// pattern) is not guaranteed to resolve before DOMContentLoaded and must
// not be used here, or Alpine.data('actionGuard', ...) may register too
// late and every x-data="actionGuard" element throws "actionGuard is not
// defined".
document.addEventListener('alpine:init', () => {
    Alpine.data('actionGuard', () => ({
        busy: false,
        _unhook: null,

        init() {
            // Safety net: some Livewire action methods never settle the
            // promise $wire.method() returns. HandleComponents::callMethods()
            // (vendor/livewire/livewire/src/Mechanisms/HandleComponents/HandleComponents.php)
            // skips the 'returns' effect for the whole request if any call
            // throws — reachable today via KycCapture::nextStep() rethrowing
            // a ValidationException out of KycStep3Trait::saveExpenses() on
            // a below-minimum-norm living-expenses amount in the legacy
            // wizard. Without this, `busy` would stay stuck true forever.
            // $wire.$hook('commit', ...) is the per-COMPONENT request
            // lifecycle hook (payload carries `component`, so $hook's own
            // id-match filter scopes it correctly) and its succeed/fail
            // fire unconditionally once this component's own request
            // finishes processing, regardless of whether 'returns' effect
            // was present. Deliberately NOT the 'request' hook — that one
            // fires at the shared network-request-pool level with no
            // `component` in its payload, so $hook's filter can't actually
            // scope it and it would fire on every component's request on
            // the page, resetting busy prematurely.
            this._unhook = this.$wire.$hook('commit', ({ succeed, fail }) => {
                succeed(() => { this.busy = false; });
                fail(() => { this.busy = false; });
            });
        },

        destroy() {
            // $hook already registers component.addCleanup(unhook), but
            // that's tied to the Livewire component's own teardown, not
            // this element's — a footer swapped out by a morph while the
            // component itself stays alive wouldn't otherwise unregister
            // this. Alpine guarantees destroy() runs when this element
            // leaves the DOM.
            if (this._unhook) this._unhook();
        },

        guard(fn) {
            if (this.busy) return;
            this.busy = true;
            Promise.resolve(fn())
                .catch((error) => console.error('[4me] guarded KYC action failed', error))
                .finally(() => { this.busy = false; });
        },
    }));
});
